/**
 * Consumer groups of the Kafka view: the Consumers tab of a topic (per group its state, members, and per partition the
 * committed offset, the end offset, the lag and the last message it consumed) and the list of every group of the
 * project's topics. An editor resets an empty group's offsets (earliest, latest or an offset) or deletes an empty
 * group, after a confirmation; a running group cannot be changed (its consumers would overwrite it).
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DataTable from '../table/DataTable';
import { ChipList } from '../table/TableParts';
import { ArrowClockwise } from '@phosphor-icons/react';
import { ConfirmDialog, DataDialog, EmptyState, ErrorState, Loading, errorText } from './DataCommon';
import { deleteConsumerGroup, listConsumerGroups, resetConsumerGroup } from '../../services/DataService';
import { ago, formatCount } from '../../utils/dataModel';
import { groupStates, plural, shortValue, topicKey } from './kafkaModel';

const lagText = (lag) => (lag === null || lag === undefined ? '—' : formatCount(lag));

export function StatePill({ state }) {
  return <span className={`data-pill ${groupStates[state] || ''}`} title="Consumer group state">{state || 'Unknown'}</span>;
}

StatePill.propTypes = { state: PropTypes.string };

const GROUP_PARTITION_COLUMNS = [
  { key: 'partition', label: 'Partition', className: 'data-mono', value: (p) => p.partition },
  { key: 'committed', label: 'Committed', className: 'data-mono', value: (p) => p.committed, render: (p) => (p.committed === null ? <span className="data-muted">none</span> : formatCount(p.committed)) },
  { key: 'high', label: 'End offset', className: 'data-mono', value: (p) => p.high, render: (p) => formatCount(p.high) },
  { key: 'lag', label: 'Lag', className: 'data-mono', value: (p) => p.lag, render: (p) => lagText(p.lag) },
];

function ResetDialog({ projectId, group, topics, onClose, onDone }) {
  const [topic, setTopic] = useState(topicKey(topics[0]));
  const [to, setTo] = useState('earliest');
  const [offset, setOffset] = useState('0');
  const [partition, setPartition] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const picked = topics.find((t) => topicKey(t) === topic) || topics[0];
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await resetConsumerGroup(projectId, group.groupId, { topic: picked, to, offset, partition });
      onDone();
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };
  const where = partition === '' ? 'every partition' : `partition ${partition}`;
  const target = to === 'offset' ? `offset ${offset}` : to === 'earliest' ? 'the oldest message still in the log' : 'the end (skip everything not read yet)';
  return (
    <DataDialog
      footer={(
        <>
          <span className="data-grow" />
          <button className="data-btn" onClick={onClose} type="button">Cancel</button>
          <button className="data-btn is-primary" disabled={busy || !picked} onClick={submit} type="button">{busy ? 'Resetting…' : 'Reset offsets'}</button>
        </>
      )}
      onClose={onClose}
      subtitle="The group's consumers start from here the next time they run."
      title={`Reset ${group.groupId}?`}
    >
      <form className="data-form-grid" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <label className="data-label" htmlFor="data-reset-topic">Topic</label>
        <select className="data-input" id="data-reset-topic" onChange={(e) => setTopic(e.target.value)} value={topic || ''}>
          {topics.map((t) => <option key={topicKey(t)} value={topicKey(t)}>{t.unprefixed ? `${t.name} (unprefixed)` : t.name}</option>)}
        </select>
        <label className="data-label" htmlFor="data-reset-to">Move to</label>
        <select className="data-input" id="data-reset-to" onChange={(e) => setTo(e.target.value)} value={to}>
          <option value="earliest">Earliest</option>
          <option value="latest">Latest</option>
          <option value="offset">An offset</option>
        </select>
        {to === 'offset' && (
          <>
            <label className="data-label" htmlFor="data-reset-offset">Offset</label>
            <input className="data-input is-narrow data-mono" id="data-reset-offset" inputMode="numeric" min={0} onChange={(e) => setOffset(e.target.value)} type="number" value={offset} />
          </>
        )}
        <label className="data-label" htmlFor="data-reset-partition">Partition</label>
        <select className="data-input" id="data-reset-partition" onChange={(e) => setPartition(e.target.value)} value={partition}>
          <option value="">All</option>
          {(picked?.partitions && Array.isArray(picked.partitions) ? picked.partitions.map((p) => p.partition) : Array.from({ length: Number(picked?.partitions) || 0 }, (_, i) => i))
            .map((p) => <option key={p} value={String(p)}>{p}</option>)}
        </select>
      </form>
      <p className="data-footnote">On {where} of {picked?.name}, {group.groupId} will next read from {target}.</p>
      {error && <p className="data-error-text" role="alert">{error}</p>}
    </DataDialog>
  );
}

ResetDialog.propTypes = {
  group: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  topics: PropTypes.array.isRequired,
};

/** Reset / Delete of one group (editors; only when the group is Empty). */
export function GroupActions({ projectId, group, topics, canWrite, onChanged }) {
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!canWrite) { return null; }
  const empty = group.state === 'Empty';
  const why = empty ? undefined : `Stop the group's consumers first: it is ${group.state}`;
  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteConsumerGroup(projectId, group.groupId);
      setDialog(null);
      onChanged();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button className="data-btn" disabled={!empty || !topics.length} onClick={() => setDialog('reset')} title={why} type="button">Reset offsets…</button>
      <button className="data-btn is-danger" disabled={!empty} onClick={() => { setError(null); setDialog('delete'); }} title={why} type="button">Delete group…</button>
      {dialog === 'reset' && <ResetDialog group={group} onClose={() => setDialog(null)} onDone={() => { setDialog(null); onChanged(); }} projectId={projectId} topics={topics} />}
      {dialog === 'delete' && (
        <ConfirmDialog busy={busy} error={error} onClose={() => setDialog(null)} onConfirm={remove} title={`Delete the group ${group.groupId}?`}>
          <p>Its committed offsets go. If its service starts again it begins as a new group (from its own auto.offset.reset).</p>
        </ConfirmDialog>
      )}
    </>
  );
}

GroupActions.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  group: PropTypes.object.isRequired,
  onChanged: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  topics: PropTypes.array.isRequired,
};

function LastConsumed({ messages }) {
  if (!messages?.length) { return null; }
  return (
    <div className="data-kafka-last">
      <h4>Last consumed</h4>
      <ul className="data-plain-list">
        {messages.map((m) => (
          <li data-testid="kafka-last-consumed" key={`${m.partition}:${m.offset}`}>
            <span className="data-muted">p{m.partition} · offset {m.offset}{m.timestamp ? ` · ${ago(m.timestamp)}` : ''}</span>
            {' '}<span className="data-mono">{m.key !== null && m.key !== undefined ? `${m.key} → ` : ''}{shortValue(m)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

LastConsumed.propTypes = { messages: PropTypes.array };

/** The Consumers tab of a topic. `consumers` is GET …/consumers. */
export function TopicConsumers({ projectId, topic, consumers, error, onRetry, canWrite, onChanged }) {
  if (error) { return <ErrorState error={error} onRetry={onRetry} />; }
  if (!consumers) { return <Loading label="Loading the consumer groups…" />; }
  if (!consumers.groups.length) {
    return (
      <EmptyState title="No consumer group yet">
        A service that consumes this topic with a group id (and commits, as auto-commit does) shows here with its lag.
      </EmptyState>
    );
  }
  return (
    <div className="data-stack">
      {consumers.groups.map((g) => (
        <section aria-label={`Group ${g.groupId}`} className="data-card" data-testid="kafka-group" key={g.groupId}>
          <div className="data-card-head">
            <h2 className="data-mono">{g.groupId}</h2>
            <StatePill state={g.state} />
            <span className="data-pill">{plural(g.members, 'member')}</span>
            <span className={`data-pill ${g.lag ? 'is-warn' : 'is-ok'}`} title="Messages the group has not consumed yet">lag {lagText(g.lag)}</span>
            <span className="data-muted">committed {formatCount(g.committed)} of {formatCount(g.end)}</span>
            <span className="data-grow" />
            <GroupActions canWrite={canWrite} group={g} onChanged={onChanged} projectId={projectId} topics={[{ ...topic, partitions: g.partitions }]} />
          </div>
          <div className="data-table-wrap">
            <DataTable
              className="data-table"
              columns={GROUP_PARTITION_COLUMNS}
              rowKey="partition"
              rows={g.partitions}
            />
          </div>
          <LastConsumed messages={g.lastConsumed} />
        </section>
      ))}
    </div>
  );
}

TopicConsumers.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  consumers: PropTypes.object,
  error: PropTypes.string,
  onChanged: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  topic: PropTypes.object.isRequired,
};

/** Every consumer group of the project's topics (the store as a whole). */
export function GroupsList({ projectId, canWrite, refreshKey = 0, onOpenTopic }) {
  const [groups, setGroups] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setError(null);
    try {
      setGroups((await listConsumerGroups(projectId)).groups || []);
    } catch (e) {
      setError(errorText(e));
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <section aria-labelledby="data-groups-title" className="data-card">
      <div className="data-card-head">
        <h2 id="data-groups-title">Consumer groups</h2>
        <span className="data-grow" />
        <button aria-label="Refresh groups" className="data-btn" onClick={load} type="button"><ArrowClockwise aria-hidden size={15} /></button>
      </div>
      {error && <ErrorState error={error} onRetry={load} />}
      {!error && !groups && <Loading label="Loading the consumer groups…" />}
      {groups && !groups.length && <EmptyState title="No consumer group yet">The groups of the services that consume these topics show here, with their lag.</EmptyState>}
      {groups && groups.length > 0 && (
        <div className="data-table-wrap">
          <DataTable
            actions={canWrite ? {
              label: 'Actions',
              width: 240,
              render: (g) => <span className="data-actions"><GroupActions canWrite group={g} onChanged={load} projectId={projectId} topics={g.topics} /></span>,
            } : undefined}
            className="data-table"
            columns={[
              { key: 'groupId', label: 'Group', className: 'data-mono', value: (g) => g.groupId },
              { key: 'state', label: 'State', width: 130, value: (g) => g.state, render: (g) => <StatePill state={g.state} /> },
              { key: 'members', label: 'Members', width: 100, value: (g) => g.members },
              {
                key: 'topics',
                label: 'Topics',
                width: 220,
                value: (g) => g.topics.length,
                title: (g) => g.topics.map((t) => `${t.name}${t.unprefixed ? '*' : ''} (lag ${lagText(t.lag)})`).join(', '),
                render: (g) => (
                  <ChipList
                    items={g.topics.map((t) => ({
                      key: topicKey(t),
                      title: `${t.name}${t.unprefixed ? '*' : ''} · lag ${lagText(t.lag)}`,
                      label: (
                        <button className="data-link data-mono data-kafka-topic-link" onClick={() => onOpenTopic(t)} type="button">
                          {t.name}{t.unprefixed ? '*' : ''} <span className="data-muted">lag {lagText(t.lag)}</span>
                        </button>
                      ),
                    }))}
                    title={`${g.groupId} reads ${plural(g.topics.length, 'topic')}`}
                  />
                ),
              },
              { key: 'lag', label: 'Lag', width: 100, className: 'data-mono', value: (g) => g.lag, render: (g) => lagText(g.lag) },
            ]}
            rowKey="groupId"
            rowProps={() => ({ 'data-testid': 'kafka-group-row' })}
            rows={groups}
            storageKey="flow.data.kafkaGroups.hiddenColumns"
          />
        </div>
      )}
    </section>
  );
}

GroupsList.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  onOpenTopic: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  refreshKey: PropTypes.number,
};
